import { Children, type ReactElement, type ReactNode, useMemo } from 'react';
import { isElement } from 'react-is';

import { _isEmptyState } from '../components/DataTableV2EmptyState.js';
import {
  _isDataTableV2SelectedRowsActions,
  type DataTableV2SelectedRowsActionsProps,
} from '../components/TableActions/DataTableV2SelectedRowsActions.js';
import {
  _isTableActions,
  type DataTableV2TableActionsProps,
} from '../components/TableActions/DataTableV2TableActions.js';
import {
  _isDataTableV2Toolbar,
  type DataTableV2ToolbarProps,
} from '../components/Toolbar/DataTableV2Toolbar.js';
import {
  _isDataTableV2Pagination,
  type DataTableV2PaginationProps,
} from '../features/Pagination/Pagination.js';
import type { DataTableV2RowData } from '../public.api.js';

/** @internal */
export type DataTableV2Slots<TData extends DataTableV2RowData> = {
  pagination: ReactElement<DataTableV2PaginationProps> | null;
  toolbarProps: DataTableV2ToolbarProps | null;
  emptyState: ReactElement | null;
  tableActions: ReactElement<DataTableV2TableActionsProps> | null;
  selectedRowsActions: ReactElement<
    DataTableV2SelectedRowsActionsProps<TData>
  > | null;
};

/**
 * @internal
 */
export function useDataTableV2Slots<TData extends DataTableV2RowData>(
  children: ReactNode,
): DataTableV2Slots<TData> {
  return useMemo(() => {
    const slots: DataTableV2Slots<TData> = {
      pagination: null,
      toolbarProps: null,
      selectedRowsActions: null,
      emptyState: null,
      tableActions: null,
    };

    Children.forEach(children, (child) => {
      if (isElement(child)) {
        if (_isDataTableV2Toolbar(child)) {
          slots.toolbarProps = child.props;
        }
        if (_isTableActions(child)) {
          slots.tableActions = child;
        }
        if (_isEmptyState(child)) {
          slots.emptyState = child;
        }
        if (_isDataTableV2Pagination(child)) {
          slots.pagination = child;
        }
        if (_isDataTableV2SelectedRowsActions(child)) {
          slots.selectedRowsActions = child;
        }
      }
    });
    return slots;
  }, [children]);
}
